import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/base/ArticleCard';
import SeoHead from '@/components/base/SeoHead';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const categoryMeta: Record<string, { title: string; description: string; keywords: string }> = {
  guide: {
    title: '平台指南 - 8866h.cc使用教程与操作指引 | 8866h官网',
    description: '全面了解8866h.cc平台功能，查看详细使用教程和操作指引。包含新手指南、功能解析、安全设置等实用内容，助您快速掌握8866h.cc平台各项功能。',
    keywords: '8866h.cc使用教程,8866h平台指南,8866h操作指引,8866h新手教程,8866h功能解析',
  },
  news: {
    title: '最新动态 - 8866h.cc平台更新与行业资讯 | 8866h官网',
    description: '获取8866h.cc平台最新更新公告、功能发布和行业动态资讯。了解平台发展动态，掌握第一手8866h.cc相关信息。',
    keywords: '8866h.cc更新,8866h平台动态,8866h最新公告,8866h行业资讯,8866h新闻',
  },
  help: {
    title: '帮助中心 - 8866h.cc常见问题与技术支持 | 8866h官网',
    description: '8866h.cc帮助中心为您提供常见问题解答、访问指南、故障排查及技术支持服务。快速解决8866h.cc使用中遇到的各类问题。',
    keywords: '8866h.cc帮助,8866h常见问题,8866h技术支持,8866h使用问题,8866h故障排查',
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'guide');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory]);

  const filteredArticles = articles.filter((a) => a.category === activeCategory);
  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const meta = categoryMeta[activeCategory] || categoryMeta.guide;

  const categorySchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, item: { '@id': 'https://8866h.cc', name: '首页' } },
        { '@type': 'ListItem', position: 2, item: { '@id': `https://8866h.cc/category/${activeCategory}`, name: currentCategory?.name || '' } },
      ],
    },
  ];

  return (
    <>
      <SeoHead
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={`https://8866h.cc/category/${activeCategory}`}
        schemaData={categorySchema}
      />

      <Navbar />

      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumbs
              items={[
                { label: '首页', path: '/' },
                { label: currentCategory?.name || '' },
              ]}
            />
            <div className="pb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3 font-heading">
                {currentCategory?.name}
              </h1>
              <p className="text-foreground-500 max-w-2xl">
                {currentCategory?.description}
              </p>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="border-b border-background-200/70 bg-background-50 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 py-3 overflow-x-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Articles Grid */}
        <section className="py-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}